import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Radio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import { ValidationErrors } from 'final-form';
import { DateTime } from 'luxon';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { AdCampaignScheduleFormValues, AdCampaignScheduleStrategy, CampaignStatus } from 'modules/campaigns/types';

import { AdCampaignDatePicker } from './AdCampaignDatePicker';

const AdCampaignScheduleFormModalContainer = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  min-width: 800px;
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledSection = styled.div`
  color: ${cbSemanticCssVars.textPrimary};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 800px;

  &:not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledSectionContent = styled.div`
  display: flex;
  align-content: center;
  align-items: flex-start;
  gap: 8px;

  > div {
    flex: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ScheduleStrategyRadioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  gap: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const StyledErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: white;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

const StyledTag = styled(Tag)`
  letter-spacing: 0.5px;
  line-height: 1em;
  margin-inline-start: 8px;
`;

const StyledDateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type AdCampaignScheduleFormModalProps = {
  errors?: ValidationErrors;
  scheduleStrategy: AdCampaignScheduleStrategy;
  campaignStatus?: CampaignStatus;
  isDisabled?: boolean;
  isDirty: boolean;
  onCloseClick(): void;
  onSubmit(): void;
};

export const AdCampaignScheduleFormModal = ({
  onSubmit,
  scheduleStrategy,
  errors,
  campaignStatus,
  onCloseClick,
  isDirty,
}: AdCampaignScheduleFormModalProps) => {
  const { formatMessage } = useIntl();
  const disabledDays = {
    before: new Date(),
  };
  const dateChangeDisabled = campaignStatus ? ['completed', 'running', 'paused'].includes(campaignStatus) : false;

  return (
    <AdCampaignScheduleFormModalContainer>
      <ModalHeader onCloseClick={onCloseClick}>
        <Typography.Heading6>
          <FormattedMessage id="campaigns.form.scheduling.label" />
        </Typography.Heading6>
      </ModalHeader>
      <StyledSection>
        <Typography.Body3>
          <FormattedMessage id="campaigns.ads.schedule.schedule-strategy-description" />
        </Typography.Body3>
        <ScheduleStrategyRadioContainer>
          <Field<AdCampaignScheduleFormValues['scheduleStrategy']>
            name="scheduleStrategy"
            type="radio"
            value="alwaysOn"
          >
            {({ input }) => {
              return (
                <Radio {...input}>
                  <Typography.Body3>
                    <FormattedMessage id="campaigns.ads.schedule.strategy-always-on-option" />
                  </Typography.Body3>
                  <StyledTag variant="lightBlack">
                    <Typography.Overline4>
                      <FormattedMessage id="campaigns.recommended" />
                    </Typography.Overline4>
                  </StyledTag>
                </Radio>
              );
            }}
          </Field>
          <Field<AdCampaignScheduleFormValues['scheduleStrategy']>
            name="scheduleStrategy"
            type="radio"
            value="selectDates"
          >
            {({ input }) => (
              <Radio {...input}>
                <Typography.Body3>
                  <FormattedMessage id="campaigns.ads.schedule.strategy-select-dates-option" />
                </Typography.Body3>
              </Radio>
            )}
          </Field>
        </ScheduleStrategyRadioContainer>
      </StyledSection>
      {scheduleStrategy === 'selectDates' && (
        <StyledSection>
          <Typography.Body2 fontWeight="bold">
            <FormattedMessage id="campaigns.form.scheduling.dates.label" />
          </Typography.Body2>

          <StyledSectionContent>
            <Field<AdCampaignScheduleFormValues['startDate']> name="startDate" type="date">
              {({ input, meta }) => {
                return (
                  <StyledDateContainer>
                    <AdCampaignDatePicker
                      {...input}
                      value={input.value ? DateTime.fromISO(input.value) : undefined}
                      onChange={value => input.onChange(value?.toISO())}
                      disabled={dateChangeDisabled}
                      placeholder={formatMessage({ id: 'campaigns.labels.start-date' })}
                      defaultValueOption="empty"
                      disabledDays={disabledDays}
                    />

                    {meta.error && (
                      <StyledErrorMessage>
                        <FormattedMessage id={meta.error} />
                      </StyledErrorMessage>
                    )}
                  </StyledDateContainer>
                );
              }}
            </Field>

            <Field<AdCampaignScheduleFormValues['endDate']> name="endDate" type="date">
              {({ input, meta }) => {
                return (
                  <StyledDateContainer>
                    <AdCampaignDatePicker
                      {...input}
                      value={
                        input.value
                          ? DateTime.fromISO(input.value).set({ hour: 11, minute: 59, second: 59, millisecond: 0 })
                          : undefined
                      }
                      onChange={value => input.onChange(value?.toISO())}
                      disabled={dateChangeDisabled}
                      placeholder={formatMessage({ id: 'campaigns.labels.end-date' })}
                      defaultValueOption="empty"
                      disabledDays={disabledDays}
                    />
                    {meta.error && (
                      <StyledErrorMessage>
                        <FormattedMessage id={meta.error} />
                      </StyledErrorMessage>
                    )}
                  </StyledDateContainer>
                );
              }}
            </Field>
          </StyledSectionContent>
        </StyledSection>
      )}
      {scheduleStrategy === 'alwaysOn' && (
        <StyledSection>
          <Typography.Body2 fontWeight="bold">
            <FormattedMessage id="campaigns.labels.start-date" />
          </Typography.Body2>

          <StyledSectionContent>
            <Field<AdCampaignScheduleFormValues['startDate']> name="startDate" type="date">
              {({ input }) => {
                return (
                  <AdCampaignDatePicker
                    {...input}
                    value={input.value ? DateTime.fromISO(input.value) : undefined}
                    onChange={value => input.onChange(value?.toISO())}
                    isInvalid={!!errors?.startDate}
                    disabled={dateChangeDisabled}
                    placeholder={formatMessage({ id: 'campaigns.labels.start-date' })}
                    defaultValueOption="today"
                    disabledDays={disabledDays}
                  />
                );
              }}
            </Field>
          </StyledSectionContent>
        </StyledSection>
      )}
      <StyledFooter>
        <Button
          disabled={!(isDirty && (!errors || !Object.keys(errors).length))}
          variant="blue"
          size="small"
          onClick={onSubmit}
        >
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </StyledFooter>
    </AdCampaignScheduleFormModalContainer>
  );
};
